import React, { useCallback, useContext, useMemo } from 'react'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import OfferFilterSelectBox from '../OfferFilterSelectBox'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { countOccupants, formatOccupantsForSelectionBox } from 'lib/offer/occupancyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { EmptyArray } from 'lib/array/arrayUtils'
import noop from 'lib/function/noop'
import { getPlural, pluralizeToString } from 'lib/string/pluralize'
import config from 'constants/config'

interface Props {
  rooms?: Array<App.Occupants>;
  open: boolean;
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer;
  inSidebar?: boolean;
  occupantUnit?: 'guest' | 'traveller';
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  multiRoomDisabled?: boolean;
  iconDisabled?: boolean;
  className?: string;
}

const OfferCapacityWithAgesSelectBox = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    open,
    rooms = EmptyArray,
    offer,
    inSidebar,
    occupantUnit = 'guest',
    onClick = noop,
    multiRoomDisabled,
    iconDisabled,
    className,
  } = props
  const dispatch = useOfferPageDispatch()
  const allRooms = useMemo(() => countOccupants(rooms), [rooms])
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const pluralOccupantUnit = getPlural(occupantUnit, 2)

  const onBoxClick = useCallback((e) => {
    if (open) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('OCCUPANCY_FILTERS_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
      trackEvent('OCCUPANCY_FILTERS_PRESSED')
    }
    onClick(e)
  }, [open, onClick, dispatch, trackEvent])

  const showInvalid = useMemo(() => !open && allRooms.childrenAge?.some(age => age === -1), [allRooms, open])
  const value = useMemo(() => {
    if (rooms.length > 0) {
      const occupantsText = formatOccupantsForSelectionBox(isTourV2Offer(offer) && offer.isLeTour, allRooms)
      const roomText = multiRoomDisabled ? '' : pluralizeToString(offer.saleUnit, rooms.length)
      return <><b>{occupantsText}</b>{roomText ? ` (${roomText})` : undefined}</>
    }
  }, [allRooms, multiRoomDisabled, offer, rooms.length])

  return (
    <OfferFilterSelectBox
      className={className}
      onClick={onBoxClick}
      active={open}
      ref={ref}
      invalid={showInvalid}
      invalidMessage="Please select children's ages"
      data-testid="OfferCapacitySelectBox"
      icon={!iconDisabled && <LineUserIcon />}
      inSidebar={inSidebar}
      label="Guests"
      placeholder={`Select ${pluralOccupantUnit}`}
      value={value}
      disabled={config.businessTraveller.isEnabled}
    />
  )
})

OfferCapacityWithAgesSelectBox.displayName = 'OfferCapacityWithAgesSelectBox'

export default withOfferPageState(state => ({
  rooms: state.rooms,
  open: state.activeFilter === 'capacity',
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
}), true)(OfferCapacityWithAgesSelectBox)
